<script setup>

import {ref} from "vue";
import SquareButton from "@/components/backlinks/opportunity/partials/SquareButton.vue";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import Swal from "sweetalert2";

const props = defineProps({
  backlink: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const backlinkUrl = ref('');
const errorMessage = ref('');
const modalOpen = ref(false);

const queryClient = useQueryClient();

const {mutate: handleProceed, isPending} = useMutation( {
  mutationFn: async () =>  await axios.post(`/ajax/v2/backlinks/opportunity/${props.backlink.id}/complete`, {
    backlink_url: backlinkUrl.value,
  }),
  onSuccess: ({data}) => {
    queryClient.invalidateQueries(['backlink-opportunity', {activeTab: 'completed'}]);
    modalOpen.value = false;
    Swal.fire({
      icon: 'success',
      title: 'Success',
      text: data.message ?? 'Your Opportunity has been moved to Completed.',
    });
  },
  onError: ({response}) => {
    errorMessage.value = 'Could not complete backlink,<br>please try again later.';
    const {data} = response;
    const {errors} = data;

    if (errors) {
      const fieldErrors = errors[Object.keys(errors)[0]]
      errorMessage.value = fieldErrors[0] ?? errorMessage
    }
  },
});
</script>

<template>
  <TableCell
    class="tw-flex tw-justify-center"
    col-span="1"
  >
    <SquareButton
      variant="manual"
      :disabled="disabled"
      @click="modalOpen = true"
    />
  </TableCell>

  <Teleport to=".tailwind">
    <BaseModal
      v-if="modalOpen"
      modal-max-width="tw-max-w-[800px]"
      @close="modalOpen = false"
    >
      <div class="tw-px-16 tw-pb-8 tw-flex tw-flex-col tw-gap-8 tw-justify-center">
        <h2 class="tw-text-2xl tw-text-center tw-font-medium">
          Manually Complete Backlink
        </h2>
        <div class="tw-flex tw-flex-col tw-space-y-[60px] tw-text-lg">
          <p class="tw-text-grey-dark tw-leading-relaxed">
            Completed backlinks in this section will be removed automatically by the system once the backlink has been
            found and confirmed. However, if you wish to speed this process up, you can manually complete this by adding
            the link url below and submitting.
          </p>
          <div class="tw-flex tw-flex-col tw-space-y-6">
            <label
              for="backlinkUrl"
              class="tw-text-grey-dark tw-text-xl"
            >
              Backlink URL:
            </label>

            <input
              id="backlinkUrl"
              v-model="backlinkUrl"
              class="tw-border-2 tw-border-grey-mid tw-rounded-lg tw-flex tw-text-black tw-w-full tw-py-2.5 tw-pl-8"
              placeholder="The URL which contains the link back to your website."
              type="text"
            >
            <div class="tw-mt-4 tw-mb-9">
              <p
                class="tw-text-base tw-text-denali"
                v-html="errorMessage"
              />
            </div>
          </div>
        </div>
        <div class="tw-w-auto tw-flex tw-space-x-8 tw-justify-end">
          <DefaultButton
            variant="base"
            @click="modalOpen = false"
          >
            Back
          </DefaultButton>
          <DefaultButton
            :disabled="!backlinkUrl"
            @click="handleProceed"
          >
            <svg-vue
              v-if="isPending"
              icon="icons/loading"
              class="tw-w-6 tw-h-6"
            />
            <span v-else>
              Save
            </span>
          </DefaultButton>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
